import React, { useContext } from "react";
import PropTypes from "prop-types";
// mui
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";

import { StoreContext } from "../../../../../../../../../../../store/StoreProvider";

import EventSelect from "./EventSelect";
import CustomPagination from "./CustomPagination";

import { prepareEditDialog } from "../../../../../../../utils/prepareEditDialog";

import deleteRowIcon from "../../../../../../../../../../../../images/ai_image/deleteRowIcon.svg";
import saveIcon from "../../../../../../../../../../../../images/ai_image/saveIcon.svg";
import cancelIcon from "../../../../../../../../../../../../images/ai_image/cancelIcon.svg";
import Act from "../../../../../../../../../../../store/actions";
import { editDialogConfig } from "../../../../../../../constants/textConfig";

const DataTable = ({ header }) => {
  const [state, dispatch] = useContext(StoreContext);
  const { sna } = state;
  const { tableData, curTablePage, itemPerPage } = sna.AI;

  const handleSave = (id) => {
    let hasEmptyValue = false;
    const newTableData = tableData.map((el) => {
      if (el.id === id && el.adding) {
        hasEmptyValue = el.srcLabel === "" || el.dstLabel === "";
        return { ...el, adding: false };
      }
      return el;
    });

    if (!hasEmptyValue) {
      dispatch({
        type: Act.SNA_SET_AI_TABLE_DATA,
        payload: newTableData,
      });
    }
  };

  const handleCancel = (id) => {
    const newTableData = tableData.filter((el) => el.id !== id);

    dispatch({
      type: Act.SNA_SET_AI_TABLE_DATA,
      payload: newTableData,
    });
  };

  const handleBlur = (e, id, type) => {
    const newTableData = tableData.map((el) => {
      if (el.id === id) {
        return { ...el, [type]: e.target.value };
      }
      return el;
    });

    dispatch({
      type: Act.SNA_SET_AI_TABLE_DATA,
      payload: newTableData,
    });
  };

  const handleDeleteRow = (id) => {
    const newTableData = tableData.filter((el) => el.id !== id);
    prepareEditDialog(dispatch, editDialogConfig.deleteRow.name, {
      newTableData,
    });
  };

  const getTextFieldStyles = (value, required) => ({
    "& .MuiInputBase-input": {
      padding: "8px",
      fontSize: "14px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: value === "" && required ? "#F83B3B" : "transparent",
        borderRadius: "8px",
      },
      "&:hover fieldset": {
        borderColor: value === "" && required ? "#F83B3B" : "#3BBFF0",
      },
      "&.Mui-focused fieldset": {
        borderColor: value === "" && required ? "#F83B3B" : "#3BBFF0",
      },
    },
  });

  const iconButtonStyles = {
    "&:active": {
      backgroundColor: "#3BBFF01A !important",
    },
    borderRadius: "6px !important",
    padding: 0,
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden", marginBottom: "16px" }}>
        <TableContainer component={Paper}>
          <Table stickyHeader sx={{ minWidth: "100%" }}>
            <TableHead>
              <TableRow>
                {header.map((head, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      backgroundColor: "#121212",
                      color: "#ffffff",
                      padding: "12px 16px",
                    }}
                    align="left"
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData
                .slice(
                  (curTablePage - 1) * itemPerPage,
                  (curTablePage - 1) * itemPerPage + itemPerPage
                )
                .map((dt) => (
                  <TableRow
                    key={dt.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell align="left" width="34%" sx={{ padding: "8px" }}>
                      <TextField
                        defaultValue={dt.source}
                        onBlur={(e) => handleBlur(e, dt.id, "source")}
                        sx={getTextFieldStyles(dt.source)}
                        placeholder="請輸入"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="left" width="12%" sx={{ padding: "8px" }}>
                      <TextField
                        defaultValue={dt.srcLabel}
                        onBlur={(e) => handleBlur(e, dt.id, "srcLabel")}
                        sx={getTextFieldStyles(dt.srcLabel, true)}
                        placeholder="請輸入"
                      />
                    </TableCell>
                    <TableCell align="left" width="12%" sx={{ padding: "8px" }}>
                      <TextField
                        defaultValue={dt.dstLabel}
                        onBlur={(e) => handleBlur(e, dt.id, "dstLabel")}
                        sx={getTextFieldStyles(dt.dstLabel, true)}
                        placeholder="請輸入"
                      />
                    </TableCell>
                    <TableCell align="left" width="12%" sx={{ padding: "8px" }}>
                      <EventSelect
                        curMainEvn={dt.mainEvn}
                        curSubEvn={dt.subEvn || ""}
                        curRowId={dt.id}
                        type="mainEvn"
                      />
                    </TableCell>
                    <TableCell align="left" width="12%" sx={{ padding: "8px" }}>
                      <EventSelect
                        curMainEvn={dt.mainEvn}
                        curSubEvn={dt.subEvn || ""}
                        curRowId={dt.id}
                        type="subEvn"
                      />
                    </TableCell>
                    <TableCell align="left" width="12%" sx={{ padding: "8px" }}>
                      <TextField
                        defaultValue={dt.relationship}
                        onBlur={(e) => handleBlur(e, dt.id, "relationship")}
                        sx={getTextFieldStyles(dt.relationship)}
                        placeholder="請輸入"
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      width="6%"
                      sx={{ padding: "8px" }}
                    >
                      {dt.adding ? (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <IconButton
                            onClick={() => handleSave(dt.id)}
                            sx={iconButtonStyles}
                          >
                            <img src={saveIcon} alt="save" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleCancel(dt.id)}
                            sx={iconButtonStyles}
                          >
                            <img src={cancelIcon} alt="cancel" />
                          </IconButton>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "end",
                          }}
                        >
                          <IconButton
                            onClick={() => handleDeleteRow(dt.id)}
                            sx={iconButtonStyles}
                          >
                            <img src={deleteRowIcon} alt="delete" />
                          </IconButton>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <CustomPagination />
    </>
  );
};

export default DataTable;

DataTable.propTypes = {
  header: PropTypes.array,
};

DataTable.defaultProps = {
  header: PropTypes.array,
};
